import { useState, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useMatch } from 'react-router-dom'
import { parseAsString, useQueryState } from 'nuqs'

import { addDays, addMonths, addWeeks } from 'date-fns'
import {
  calendarWeek,
  calendarMonth,
  newCalendarAllChannels,
  newCalendarSingleChannel,
  scheduleCalendar,
} from '~publish/legacy/routes'

export type CalendarGranularity = 'week' | 'month' | 'three-day'

export type UseNavigationReturn = {
  leftAnimation: boolean
  rightAnimation: boolean
  currentDate: Date
  setCurrentDate: (date: Date) => void
  setLeftAnimation: (leftAnimation: boolean) => void
  setRightAnimation: (rightAnimation: boolean) => void
  onNavigate: (
    direction: 'forwards' | 'backwards',
    granularity: CalendarGranularity,
  ) => void
  granularity: CalendarGranularity
  navigateGranularity: (granularity: CalendarGranularity) => void
}

const getCurrentGranularity = ({
  isWeekView,
  isCalendarRoute,
  granularity,
}: {
  isWeekView: boolean
  isCalendarRoute: boolean
  granularity?: string
}): CalendarGranularity => {
  if (isWeekView || (isCalendarRoute && granularity === 'week')) {
    return 'week'
  }

  if (isCalendarRoute && granularity === 'three-day') {
    return 'three-day'
  }

  return 'month'
}

const useGranularity = (): [
  CalendarGranularity,
  (granularity: CalendarGranularity) => void,
] => {
  const navigate = useNavigate()
  const isWeekView = !!useMatch(calendarWeek.route)
  const isAllChannels = !!useMatch(newCalendarAllChannels.route)
  const isSingleChannel = !!useMatch(newCalendarSingleChannel.route)
  const isScheduleCalendar = !!useMatch(scheduleCalendar.route)
  const { granularity: routeGranularity, id: channelId } = useParams<{
    granularity?: string
    id?: string
  }>()
  const isCalendarRoute = isAllChannels || isSingleChannel || isScheduleCalendar
  const currentGranularity = getCurrentGranularity({
    isWeekView,
    isCalendarRoute,
    granularity: routeGranularity,
  })

  const navigateGranularity = useCallback(
    (granularity: CalendarGranularity) => {
      if (isAllChannels) {
        return navigate(newCalendarAllChannels.getRoute(granularity, {}, true))
      }
      if (isSingleChannel) {
        if (channelId) {
          return navigate(
            newCalendarSingleChannel.getRoute(channelId, granularity, {}, true),
          )
        }
        return navigate(newCalendarAllChannels.getRoute(granularity, {}, true))
      }
      if (isScheduleCalendar) {
        return navigate(scheduleCalendar.getRoute(granularity, {}, true))
      }
      if (granularity === 'month') {
        return navigate(calendarMonth.getRoute({}, true))
      }
      return navigate(calendarWeek.getRoute({}, true))
    },
    [navigate, isAllChannels, isSingleChannel, isScheduleCalendar, channelId],
  )

  return [currentGranularity, navigateGranularity]
}

const useInnerNavigation = (
  dateParam: string | null | undefined,
  setDateParam: (date: string) => void,
): Omit<UseNavigationReturn, 'granularity' | 'navigateGranularity'> => {
  const currentDate = useMemo(() => {
    if (dateParam) {
      const unitxTime = parseInt(dateParam, 10)
      return new Date(unitxTime)
    }
    return new Date()
  }, [dateParam])
  const setCurrentDate = useCallback(
    (date: Date) => {
      setDateParam(date.getTime().toString())
    },
    [setDateParam],
  )
  const [leftAnimation, setLeftAnimation] = useState(false)
  const [rightAnimation, setRightAnimation] = useState(false)

  const onNavigate = useCallback(
    (
      direction: 'forwards' | 'backwards',
      granularity: CalendarGranularity,
    ): void => {
      const delta = direction === 'forwards' ? 1 : -1
      const newDate =
        granularity === 'week'
          ? addWeeks(currentDate, delta)
          : granularity === 'three-day'
            ? addDays(currentDate, delta * 3)
            : addMonths(currentDate, delta)
      setCurrentDate(newDate)
      if (direction === 'forwards') {
        setRightAnimation(true)
      } else {
        setLeftAnimation(true)
      }
    },
    [currentDate, setCurrentDate, setRightAnimation, setLeftAnimation],
  )

  return {
    leftAnimation,
    rightAnimation,
    currentDate,
    setCurrentDate,
    setLeftAnimation,
    setRightAnimation,
    onNavigate,
  }
}

export const useNavigation = (): UseNavigationReturn => {
  const [dateParam, setDateParam] = useQueryState('date', parseAsString)
  const coreNavigation = useInnerNavigation(
    dateParam,
    (date) => void setDateParam(date),
  )
  const [granularity, navigateGranularity] = useGranularity()
  return useMemo(() => {
    return {
      ...coreNavigation,
      granularity,
      navigateGranularity,
    }
  }, [coreNavigation, granularity, navigateGranularity])
}

export const testUseNavigation = useInnerNavigation
export const testGetCurrentGranularity = getCurrentGranularity
